import React, { useState, useEffect, useRef } from 'react';
import { useLoading } from '../contexts/LoadingContext';

/**
 * LoadingBorder - An animated border that shows when backend is processing
 * Wraps around individual form elements that trigger backend calls
 *
 * @param {string} fieldName - The name of the field this border wraps
 * @param {boolean} backendEnabled - If true, shows continuous loading during backend calls
 * @param {boolean} frontendAnimation - If true, shows single-rotation animation on value change
 * @param {any} triggerValue - Optional value to watch for changes (triggers frontend animation)
 */
export const LoadingBorder = ({
  children,
  className = '',
  fieldName,
  backendEnabled = false,
  frontendAnimation = false,
  triggerValue
}) => {
  const { loadingField, isBackendLoading } = useLoading();
  const [showFrontendAnimation, setShowFrontendAnimation] = useState(false);
  const isFirstRender = useRef(true);
  const prevTriggerValue = useRef(triggerValue);

  // Track value changes for frontend animation
  useEffect(() => {
    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevTriggerValue.current = triggerValue;
      return;
    }

    // Only trigger if triggerValue actually changed
    if (frontendAnimation && !isBackendLoading && triggerValue !== prevTriggerValue.current) {
      prevTriggerValue.current = triggerValue;
      setShowFrontendAnimation(true);
      // Single rotation takes 1.2s, stop after that
      const timer = setTimeout(() => {
        setShowFrontendAnimation(false);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [triggerValue, frontendAnimation, isBackendLoading]);

  // Backend loading: show continuous animation for the field that triggered it
  const showBackendLoading = backendEnabled && loadingField === fieldName;

  // Determine which animation class to use
  const animationClass = showBackendLoading
    ? 'loading'
    : showFrontendAnimation
      ? 'loading-single'
      : '';

  return (
    <div className={`loading-border-container ${animationClass} ${className}`}>
      {children}
    </div>
  );
};
